/* eslint-disable */
import {createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {WithChildren} from '../../helpers';
import useWindowDimensions from '../../hooks/useWindowDimensions';

import {CampaignInfoTypes} from '../../../modules/campaign/pages/details/Types';

type NavBackButtonAction = undefined | (() => void);

interface LayoutContextProps {
	isMobile: boolean;
	shouldShowSidebarToggleButton: boolean;
	shouldShowBackButtonInNavbar: boolean;
	shouldShowNavbarAction: boolean;
	isSidebarShown: boolean;
	showAuthNavbar: boolean;
	campaignInfo: CampaignInfoTypes | undefined;
	navBarAction: ReactNode | null;
	hasFixedContentUnderHeader: boolean;
	hasNoficiationInNavbar: boolean;
	toggleSidebar: () => void;
	closeSidebar: () => void;
	disableAuthNavbar: () => void;
	setShowAuthNavbar: (show: boolean) => void;
	setHasBackButtonInNavbar: (val: boolean) => void;
	navBackButtonAction: NavBackButtonAction;
	setNavBackButtonAction: (action: NavBackButtonAction) => void;
	setNavBarAction: (navBarAction: ReactNode | null) => void;
	setShouldShowNavbarAction: (show: boolean) => void;
	setHasNoficiationInNavbar: (show: boolean) => void;
	setHasFixedContentUnderHeader: (show: boolean) => void;
	headerHeight: number;
	setHeaderHeight: (height: number) => void;
	tabNumber: number;
	setTabNumber: (tab: number) => void;
	setCampaignInfo: (info: CampaignInfoTypes | undefined) => void;
}

const LayoutContext = createContext<LayoutContextProps>({
	campaignInfo: undefined,
	isMobile: false,
	shouldShowSidebarToggleButton: false,
	shouldShowBackButtonInNavbar: false,
	shouldShowNavbarAction: false,
	isSidebarShown: false,
	navBarAction: null,
	showAuthNavbar: false,
	hasFixedContentUnderHeader: false,
	hasNoficiationInNavbar: false,
	toggleSidebar: () => {},
	closeSidebar: () => {},
	disableAuthNavbar: () => {},
	setShowAuthNavbar: (show: boolean) => {
		console.log(show);
	},
	setHasBackButtonInNavbar: (val: boolean) => {
		console.log(val);
	},
	navBackButtonAction: () => {},
	setNavBackButtonAction: (action: NavBackButtonAction) => {
		console.log(action);
	},
	setNavBarAction: () => {},
	setShouldShowNavbarAction: () => {},
	setHasFixedContentUnderHeader: (val: boolean) => {
		console.log(val);
	},
	setHasNoficiationInNavbar: (val: boolean) => {
		console.log(val);
	},
	headerHeight: 0,
	setHeaderHeight: (height: number) => {
		console.log(height);
	},
	tabNumber: 0,
	setTabNumber: (tab: number) => {
		console.log(tab);
	},
	setCampaignInfo: (info: CampaignInfoTypes | undefined) => {
		console.log(info);
	},
});

const disableSplashScreen = () => {
	const splashScreen = document.getElementById('splash-screen');
	if (splashScreen) {
		splashScreen.style.setProperty('display', 'none');
	}
};

const LayoutProvider: FC<WithChildren> = ({children}) => {
	const [isSidebarShown, setIsSidebarShown] = useState(false);
	const [showAuthNavbar, setShowAuthNavbar] = useState(true);
	const [hasBackButtonInNavbar, setHasBackButtonInNavbar] = useState(false);
	const [hasNoficiationInNavbar, setHasNoficiationInNavbar] = useState(false);
	const [navBackButtonAction, setNavBackButtonAction] = useState<NavBackButtonAction>(undefined);
	const [navBarAction, setNavBarAction] = useState<ReactNode | null>(null);
	const [shouldShowNavbarAction, setShouldShowNavbarAction] = useState(false);
	const [hasFixedContentUnderHeader, setHasFixedContentUnderHeader] = useState(false);
	const [headerHeight, setHeaderHeight] = useState(0);
	const [tabNumber, setTabNumber] = useState(0);
	const [campaignInfo, setCampaignInfo] = useState<CampaignInfoTypes | undefined>();

	const {width} = useWindowDimensions();

	useEffect(() => {
		disableSplashScreen();
	}, []);

	const isMobile = useMemo(() => {
		return width < 640;
	}, [width]);

	useEffect(() => {
		setHasNoficiationInNavbar(!isMobile);
	}, [isMobile, setHasNoficiationInNavbar]);

	const shouldShowSidebarToggleButton = useMemo(() => {
		return isMobile && !hasBackButtonInNavbar;
	}, [isMobile, hasBackButtonInNavbar]);

	const shouldShowBackButtonInNavbar = useMemo(() => {
		return isMobile && hasBackButtonInNavbar;
	}, [isMobile, hasBackButtonInNavbar]);

	const toggleSidebar = useCallback(() => {
		setIsSidebarShown((current) => !current);
	}, []);

	const closeSidebar = useCallback(() => {
		setIsSidebarShown(false);
	}, []);

	const disableAuthNavbar = useCallback(() => {
		setShowAuthNavbar((current) => !current);
	}, []);

	const value = useMemo(() => {
		return {
			isMobile,
			shouldShowSidebarToggleButton,
			shouldShowBackButtonInNavbar,
			isSidebarShown,
			showAuthNavbar,
			navBarAction,
			shouldShowNavbarAction,
			hasFixedContentUnderHeader,
			tabNumber,
			setTabNumber,
			setNavBarAction,
			toggleSidebar,
			closeSidebar,
			disableAuthNavbar,
			setShowAuthNavbar,
			setHasBackButtonInNavbar,
			navBackButtonAction,
			setNavBackButtonAction,
			setShouldShowNavbarAction,
			setHasFixedContentUnderHeader,
			headerHeight,
			setHeaderHeight,
			campaignInfo,
			setCampaignInfo,
			hasNoficiationInNavbar,
			setHasNoficiationInNavbar,
		};
	}, [
		isMobile,
		shouldShowSidebarToggleButton,
		shouldShowBackButtonInNavbar,
		shouldShowNavbarAction,
		isSidebarShown,
		showAuthNavbar,
		navBarAction,
		hasFixedContentUnderHeader,
		tabNumber,
		setTabNumber,
		setNavBarAction,
		toggleSidebar,
		closeSidebar,
		disableAuthNavbar,
		setShowAuthNavbar,
		setHasBackButtonInNavbar,
		navBackButtonAction,
		setNavBackButtonAction,
		setShouldShowNavbarAction,
		setHasFixedContentUnderHeader,
		headerHeight,
		setHeaderHeight,
		campaignInfo,
		setCampaignInfo,
		hasNoficiationInNavbar,
		setHasNoficiationInNavbar,
	]);

	return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export function useLayout() {
	return useContext(LayoutContext);
}

const NavbarBackButton: FC<{action?: NavBackButtonAction}> = ({action}) => {
	const {isMobile, setHasBackButtonInNavbar, setNavBackButtonAction} = useLayout();
	useEffect(() => {
		if (isMobile) {
			setHasBackButtonInNavbar(true);
			if (action) setNavBackButtonAction(action);
		}
		return () => {
			setHasBackButtonInNavbar(false);
		};
	}, [isMobile, setHasBackButtonInNavbar, action, setNavBackButtonAction]);
	return null;
};

const HasFixedContentUnderHeader: FC<{mobileOnly?: boolean}> = ({mobileOnly = true}) => {
	const {isMobile, setHasFixedContentUnderHeader} = useLayout();
	useEffect(() => {
		if (!mobileOnly || isMobile) {
			setHasFixedContentUnderHeader(true);
		}
		return () => {
			setHasFixedContentUnderHeader(false);
		};
	}, [isMobile, mobileOnly, setHasFixedContentUnderHeader]);
	return null;
};

const NavbarNotification: FC<{mobileOnly?: boolean}> = ({mobileOnly = true}) => {
	const {isMobile, setHasNoficiationInNavbar} = useLayout();
	useEffect(() => {
		if (!mobileOnly || isMobile) {
			setHasNoficiationInNavbar(true);
		}
		return () => {
			setHasNoficiationInNavbar(false);
		};
	}, [isMobile, mobileOnly, setHasNoficiationInNavbar]);
	return null;
};

export {LayoutContext, LayoutProvider, NavbarBackButton, HasFixedContentUnderHeader, NavbarNotification};
